import { useMutation } from "@tanstack/react-query";
import Swal from "sweetalert2";
import { api } from "../../libs/axios";
import { AxiosErrorResponse } from "../pdv/mutationReserva";
import { PedidoStatusCode, BilheteStatusCode } from "../../utils/interfaces";
import { queryClient } from "../../libs/queryClient";
import { getUser } from "../../libs/auth";
import { formatBRL } from "../../utils/currency";
import { isMultaNaFatura } from "../../utils/multaCancelamento";

const flattenBilhetes = (bilhetes: any): any[] => {
  if (!bilhetes) return [];

  return Object.values(bilhetes).flatMap((grupos: any) =>
    (grupos || []).flatMap((grupo: any) =>
      (grupo.viagens || []).flatMap((viagem: any) => viagem.bilhetes || [])
    )
  );
};

const bilheteStatusFinalizados = [
  BilheteStatusCode.Cancelado,
  BilheteStatusCode.Devolvido,
  BilheteStatusCode.EstornoPendente,
];

type UseMutationCancelarOptions = {
  redirectOnSuccess?: string;
};

const flattenBilhetesResposta = (bilhetes: any): any[] => {
  if (!bilhetes) return [];

  if (Array.isArray(bilhetes)) {
    if (bilhetes.some((item: any) => Array.isArray(item?.viagens))) {
      return bilhetes.flatMap((grupo: any) =>
        (grupo.viagens || []).flatMap((viagem: any) => viagem.bilhetes || [])
      );
    }

    return bilhetes;
  }

  return Object.values(bilhetes).flatMap((grupos: any) =>
    (grupos || []).flatMap((grupo: any) =>
      (grupo.viagens || []).flatMap((viagem: any) => viagem.bilhetes || [])
    )
  );
};

const totalMultaBilhetes = (bilhetes: any[]): number =>
  bilhetes.reduce((total, bilhete) => {
    const multa = Number(bilhete?.multa ?? bilhete?.multa_cancelamento ?? 0);

    return total + (Number.isFinite(multa) ? multa : 0);
  }, 0);

export function useMutationCancelar(options: UseMutationCancelarOptions = {}) {
  const isDevolucaoStatus = (status: number | undefined) =>
    [PedidoStatusCode.Finalizado, PedidoStatusCode.DevolvidoParcialmente].includes(status as PedidoStatusCode);

  return useMutation({
    mutationFn: async (id: string) => {
      const pedidoCache = queryClient.getQueryData(['pedido', { id }]) as any;
      const pedidoDetalhesCache = queryClient.getQueryData(['pedido-detalhes', { id }]) as any;
      const statusPedidoAtual = pedidoCache?.pedido?.status ?? pedidoDetalhesCache?.pedido?.status;
      const isDevolucao = isDevolucaoStatus(statusPedidoAtual);

      const bilhetesPedido = pedidoCache?.bilhetes
        ? flattenBilhetes(pedidoCache.bilhetes)
        : (pedidoDetalhesCache?.bilhetes || []);

      const temBilhetes = (bilhetesPedido || []).length > 0;
      const bilhetesAbertos = (bilhetesPedido || []).filter(
        (b: any) => !bilheteStatusFinalizados.includes(b.status)
      );
      const bilhetesAbertosIds = bilhetesAbertos.map((b: any) => b.id);
      const possuiDevolucaoParcial = temBilhetes
        && bilhetesAbertos.length > 0
        && bilhetesAbertos.length !== (bilhetesPedido || []).length;

      if (temBilhetes && (possuiDevolucaoParcial || bilhetesAbertos.length === 0)) {
        if (bilhetesAbertos.length > 0) {
          await Promise.all(
            bilhetesAbertos.map((b: any) =>
              api.delete(`/pedido/cancelar/bilhete/${b.id}`)
            )
          );
        }

        return {
          cancelledIndividually: true,
          bilheteIds: bilhetesAbertosIds,
          isDevolucao,
        };
      }

      const response = await api.delete(`/pedido/${id}/cancelar`);

      return {
        cancelledIndividually: false,
        bilheteIds: bilhetesAbertosIds,
        isDevolucao,
        response: response.data,
      };
    },
    onSuccess: async (result, id) => {
      const pedidoCache = queryClient.getQueryData(['pedido', { id }]) as any;
      const pedidoDetalhesCache = queryClient.getQueryData(['pedido-detalhes', { id }]) as any;
      const pedidoAtual = pedidoCache?.pedido ?? pedidoDetalhesCache?.pedido;
      const cancelledIndividually = !!(result as any)?.cancelledIndividually;
      const bilheteIds: string[] = (result as any)?.bilheteIds || [];
      const isDevolucao = !!(result as any)?.isDevolucao;
      const multaFaturada = isDevolucao
        && isMultaNaFatura(pedidoAtual, Boolean(getUser()?.agencia?.repassar_multa));

      const novoStatusBilhete = isDevolucao
        ? BilheteStatusCode.Devolvido
        : BilheteStatusCode.Cancelado;
      const novoLabelBilhete = isDevolucao ? 'DEVOLVIDO' : 'CANCELADO';

      if (cancelledIndividually) {
        queryClient.setQueryData(['pedido', { id }], (data: any) => {
          if (!data) return data;

          const bilhetesAtualizados = Object.fromEntries(
            Object.entries(data.bilhetes || {}).map(([contexto, grupos]: any) => {
              const gruposAtualizados = (grupos || []).map((grupo: any) => ({
                ...grupo,
                viagens: (grupo.viagens || []).map((viagem: any) => ({
                  ...viagem,
                  bilhetes: (viagem.bilhetes || []).map((b: any) =>
                    bilheteIds.includes(b.id)
                      ? {
                          ...b,
                          status: novoStatusBilhete,
                          status_label: novoLabelBilhete,
                        }
                      : b
                  ),
                })),
              }));

              return [contexto, gruposAtualizados];
            })
          );

          return {
            ...data,
            bilhetes: bilhetesAtualizados,
          };
        });

        queryClient.setQueryData(['pedido-detalhes', { id }], (data: any) => {
          if (!data) return data;

          return {
            ...data,
            bilhetes: (data.bilhetes || []).map((b: any) =>
              bilheteIds.includes(b.id)
                ? {
                    ...b,
                    status: novoStatusBilhete,
                    status_label: novoLabelBilhete,
                  }
                : b
            ),
          };
        });
      } else {
        // Atualiza cache local para refletir devolução/cancelamento conforme status atual do pedido/bilhetes
        queryClient.setQueryData(['pedido', { id }], (data: any) => {
          if (!data) return data;

          const isDevolucaoPedido = isDevolucaoStatus(data.pedido?.status);

          const statusBilhete = isDevolucaoPedido
            ? BilheteStatusCode.Devolvido
            : BilheteStatusCode.Cancelado;
          const statusLabelBilhete = isDevolucaoPedido ? 'DEVOLVIDO' : 'CANCELADO';

          const bilhetesAtualizados = Object.fromEntries(
            Object.entries(data.bilhetes || {}).map(([contexto, grupos]: any) => {
              const gruposAtualizados = (grupos || []).map((grupo: any) => ({
                ...grupo,
                viagens: (grupo.viagens || []).map((viagem: any) => ({
                  ...viagem,
                  bilhetes: (viagem.bilhetes || []).map((b: any) => ({
                    ...b,
                    status: statusBilhete,
                    status_label: statusLabelBilhete,
                  })),
                })),
              }));

              return [contexto, gruposAtualizados];
            })
          );

          return {
            ...data,
            pedido: {
              ...data.pedido,
              status: isDevolucaoPedido ? PedidoStatusCode.Devolvido : PedidoStatusCode.Cancelado,
              status_label: isDevolucaoPedido ? 'DEVOLVIDO' : 'CANCELADO',
            },
            bilhetes: bilhetesAtualizados,
          };
        });

        // Garante consistência também no detalhamento (estrutura diferente)
        queryClient.setQueryData(['pedido-detalhes', { id }], (data: any) => {
          if (!data) return data;

          const isDevolucaoPedido = isDevolucaoStatus(data.pedido?.status);

          return {
            ...data,
            pedido: {
              ...data.pedido,
              status: isDevolucaoPedido ? PedidoStatusCode.Devolvido : PedidoStatusCode.Cancelado,
              status_label: isDevolucaoPedido ? 'DEVOLVIDO' : 'CANCELADO',
            },
            bilhetes: (data.bilhetes || []).map((b: any) => ({
              ...b,
              status: isDevolucaoPedido ? BilheteStatusCode.Devolvido : BilheteStatusCode.Cancelado,
              status_label: isDevolucaoPedido ? 'DEVOLVIDO' : 'CANCELADO',
            })),
          };
        });
      }

      queryClient.invalidateQueries({
        queryKey: ['pedido'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['pedido-detalhes'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['pedidos'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['faturas'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['fatura'],
        exact: false,
      });

      let multaDevolucao = 0;
      if (isDevolucao && Boolean(pedidoAtual?.repassar_multa ?? getUser()?.agencia?.repassar_multa)) {
        try {
          const pedidoAtualizado = await api.get(`/pedido/${id}`);
          multaDevolucao = totalMultaBilhetes(
            flattenBilhetesResposta(pedidoAtualizado.data?.bilhetes)
          );
        } catch {
          multaDevolucao = 0;
        }
      }

      await Swal.fire({
        title: 'Pedido Devolvido/Cancelado',
        icon: 'success',
        html: [
          cancelledIndividually
            ? 'Bilhetes selecionados devolvidos. Os demais permanecem com a cor original.'
            : 'O pedido e os bilhetes vão ficar com o status DEVOLVIDO.',
          multaDevolucao > 0
            ? multaFaturada
              ? `A taxa de ${formatBRL(multaDevolucao)} foi lançada na sua fatura.`
              : `Essa devolução teve uma taxa no valor de ${formatBRL(multaDevolucao)}.`
            : '',
        ].filter(Boolean).join('<br><br>'),
      });

      if (options.redirectOnSuccess) {
        window.location.assign(options.redirectOnSuccess);
      }
    },
    onError: (err: AxiosErrorResponse) => {
     const requestId = err.response?.headers?.['request-id'];
     
      Swal.fire({
        title: "Erro ao cancelar pedido",
        icon: "error",
        text: `${err.response?.data?.data?.message || "Erro desconhecido"} ${requestId ? `- Request ID: ${requestId}` : ''}`,
      })
    }
  });
}
